import { TeamRole } from "@repo/api/team-roles";
import { teamQueryKeys } from "@/lib/queries/team-queries";
import { TeamMembersDto } from "@repo/api/team";
import {
  useDeleteInviteMutation,
  useUpdateInviteMutation,
} from "@/lib/queries/invite-queries";
import Toast from "@/lib/components/Toast";
import { queryClient } from "@/lib/queries/query-client";
import { MemberItem } from "./MemberItem";

export default function InvitedMembers({
  isMobile = false,
  teamExternalId,
  invites,
  userIsAllowedToEdit,
}: {
  isMobile?: boolean;
  teamExternalId: string;
  invites: TeamMembersDto["invites"];
  userIsAllowedToEdit: boolean;
}) {
  const { mutate: updateInvite } = useUpdateInviteMutation({
    teamExternalId,
    onMutate: async ({ inviteExternalId, data }) => {
      await queryClient.cancelQueries({
        queryKey: teamQueryKeys.getTeamMembers(teamExternalId),
      });

      const previousData = queryClient.getQueryData<TeamMembersDto>(
        teamQueryKeys.getTeamMembers(teamExternalId),
      );

      if (previousData) {
        const updatedInvites = previousData.invites.map((invite) =>
          invite.externalId === inviteExternalId
            ? { ...invite, ...data }
            : invite,
        );

        queryClient.setQueryData(teamQueryKeys.getTeamMembers(teamExternalId), {
          ...previousData,
          invites: updatedInvites,
        });
      }

      return { previousData };
    },
    onError: (_error, _variables, context) => {
      if (context?.previousData) {
        queryClient.setQueryData(
          teamQueryKeys.getTeamMembers(teamExternalId),
          context.previousData,
        );
      }

      Toast.error("Failed to update invite");
    },
  });

  const { mutate: deleteInvite } = useDeleteInviteMutation({
    teamExternalId,
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: teamQueryKeys.getTeamMembers(teamExternalId),
        type: "all",
      });
    },
    onError: () => {
      Toast.error("Failed to delete invite");
    },
  });

  const onRoleChange = (inviteExternalId: string, role: TeamRole) => {
    updateInvite({
      inviteExternalId,
      data: { role },
    });
  };

  const onDelete = (inviteExternalId: string) => {
    deleteInvite(inviteExternalId);
  };

  const invitesSortedAlphabetically = [...invites].sort((a, b) =>
    a.email.localeCompare(b.email),
  );

  return (
    <>
      {invitesSortedAlphabetically.map((invite) => (
        <MemberItem
          isMobile={isMobile}
          key={invite.externalId}
          status="Invited"
          item={{
            id: invite.externalId,
            role: invite.role,
            email: invite.email,
          }}
          userIsAllowedToEdit={userIsAllowedToEdit}
          onRoleChange={onRoleChange}
          onDelete={onDelete}
        />
      ))}
    </>
  );
}
